/**
 * THE LIVE SMOKE TEST FAMILY (Bobbie, 2026-10-24: "Clearly we need more
 * smokescreen testing", then "test as a parent because that seems to be where
 * the issues are"). A robot signs in to the real Hub as a hidden test parent
 * and her hidden test dancer, in one hidden test class, and does what families
 * do: upload a video, play it, save it. hub/tests/live-smoke/README.md has the
 * whole picture.
 *
 * The three fixture docs carry `canary: true`, the SAME invisibility flag the
 * video pipeline canary uses (docs/video-robustness-plan-2026-08-18.md section
 * 3), so every listing, roster, directory, report and notification lane that
 * already drops the canary drops them too. That alone would also hide the
 * class from the test family itself, which is the one viewer that must see
 * it, so the smoke docs add `smokeViewerUids`: the exact uids the robot signs
 * in as. A canary doc stays hidden from everyone else. Only the server-side
 * fixture script (Admin SDK) ever writes that field; no screen in the Hub
 * offers it, so a real person's uid can never land in it.
 *
 * The video pipeline canary's own fixture has no `smokeViewerUids`, so it stays
 * hidden from every viewer exactly as before.
 */
export function isHiddenFixtureData(value: unknown, viewerUid: string | null | undefined): boolean {
  if (!value || typeof value !== 'object') return false;
  const data = value as Record<string, unknown>;
  if (data.canary !== true) return false;
  if (!viewerUid) return true;
  const viewers = data.smokeViewerUids;
  return !(Array.isArray(viewers) && viewers.includes(viewerUid));
}

/**
 * The two test classes. A staff inbox reads class posts studio wide, with no
 * class filter of its own, so a "New video posted" note the robot's upload
 * made would land in every manager's and front desk's Messages and Search
 * (refute round 2 on PR #1420). Their posts are dropped by class id, which
 * the post itself carries, rather than by a lookup of the class.
 */
export const HIDDEN_FIXTURE_OFFERING_IDS: ReadonlySet<string> = new Set([
  'canary-video-fixture',
  'smoke-fixture-class',
]);

export function isHiddenFixturePost(post: { offeringId?: unknown }): boolean {
  return typeof post.offeringId === 'string' && HIDDEN_FIXTURE_OFFERING_IDS.has(post.offeringId);
}
